import { useEffect, useMemo, useRef, useState } from "react";
import { Flex } from "styles/utility-components";

import { PdfViewerContainer } from "components/document_details/PdfViewerContainer";
import { DocumentFilters, getDocumentViewerSource } from "components/documents/utils";
import { PaywallBanner } from "components/paywall/PaywallBanner";
import Button from "components/ui/button/Button";
import Drawer from "components/ui/drawer/Drawer";
import EmptyState from "components/ui/empty_state/EmptyState";
import Tabs from "components/ui/tabs/Tabs";
import Tooltip from "components/ui/tooltip/Tooltip";
import { FileViewerContextProvider, useFileViewerInstance } from "lib/core_components/FileViewer";
import { drawSourceAnnotations, SourceLocation } from "lib/core_components/fileViewerAnnotations";
import { DOCUMENT_SINGLE_QA, DOCUMENTS_AI_FEATURES, useVariableValue } from "lib/featureFlags";
import { useDocument } from "lib/hooks/api/documents/useDocument";
import { useLogEntityViewOnOpen } from "lib/hooks/api/userActivity/useLogEntityView";
import { useSubscription } from "lib/hooks/subscription";
import { TrackingProvider } from "lib/tracking";
import { LoggedEntityType } from "lib/types/graphQLEnums";
import DocumentHeader from "./DocumentHeader";
import { DocumentViewerFilters } from "./FilterForm";
import {
  createTabItems,
  createTabPanels,
  TAB_KEYS,
  TAB_LABELS,
  TabKey,
  ToolbarContainerLegacy,
  ToolbarContainerNew,
} from "./utils";

import css from "./DocumentDrawer.module.scss";

type DocumentDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId?: string;
  filters: DocumentFilters;
  disablePermissionCheck?: boolean;
  onPreviousDocument?: () => void;
  onNextDocument?: () => void;
  canGoToPreviousDocument?: boolean;
  canGoToNextDocument?: boolean;
  /**
   * Locations to highlight (and jump the viewer to) on first document load.
   * Reuses `drawSourceAnnotations`; pass `bbox: null` for page-only jumps.
   */
  initialSourceLocations?: SourceLocation[];
};

type DocumentViewerProps = {
  documentId: string;
  filters: DocumentFilters;
  isDocumentsAiEnabled: boolean;
  isDocumentAiSearchEnabled: boolean;
  initialSourceLocations?: SourceLocation[];
};

export default function DocumentDrawer({
  documentId,
  filters,
  open,
  onOpenChange,
  disablePermissionCheck = false,
  onPreviousDocument = () => undefined,
  onNextDocument = () => undefined,
  canGoToPreviousDocument = false,
  canGoToNextDocument = false,
  initialSourceLocations,
}: DocumentDrawerProps) {
  const subscription = useSubscription();
  const hasDocuments = disablePermissionCheck ? true : subscription.hasDataTypes("DOCUMENTS");
  const isDocumentsAiEnabled = useVariableValue(DOCUMENTS_AI_FEATURES, false);
  const isDocumentAiSearchEnabled = useVariableValue(DOCUMENT_SINGLE_QA, false);

  const { data, isError, isLoading } = useDocument(documentId ?? "", {
    enabled: !!documentId && hasDocuments,
  });

  return (
    <TrackingProvider
      data={{ "Context source": "Document preview", "Document type": data?.category ?? "-" }}
    >
      <Drawer open={open} onOpenChange={onOpenChange} width={1200}>
        <div className={css.previewWrapper}>
          <div className={css.controls}>
            <Button
              buttonType="secondary"
              title="Close"
              aria-label="Close document drawer"
              icon="chevronRightDouble"
              onClick={() => onOpenChange(false)}
              className={css.controlsBtn}
            />
            <div className={css.navigationControls}>
              <Tooltip
                content={canGoToPreviousDocument ? "Previous document" : "No previous document"}
                delayDuration={0}
              >
                <span
                  className={css.navigationTooltipTrigger}
                  data-testid="previous-document-tooltip-trigger"
                >
                  <Button
                    buttonType="secondary"
                    aria-label="Previous document"
                    icon="chevronUp"
                    onClick={onPreviousDocument}
                    className={css.controlsBtn}
                    disabled={!canGoToPreviousDocument}
                  />
                </span>
              </Tooltip>
              <Tooltip
                content={canGoToNextDocument ? "Next document" : "No next document"}
                delayDuration={0}
              >
                <span
                  className={css.navigationTooltipTrigger}
                  data-testid="next-document-tooltip-trigger"
                >
                  <Button
                    buttonType="secondary"
                    aria-label="Next document"
                    icon="chevronDown"
                    onClick={onNextDocument}
                    className={css.controlsBtn}
                    disabled={!canGoToNextDocument}
                  />
                </span>
              </Tooltip>
            </div>
          </div>
          {!hasDocuments ? (
            <PaywallBanner featureType="DOCUMENTS" />
          ) : isError ? (
            <Flex alignItems="center" justifyContent="center" width="100%">
              <EmptyState
                type="loadError"
                heading="Sorry something has gone wrong loading this document"
                description="If this error persists please contact support"
                width="100%"
                size="large"
              />
            </Flex>
          ) : (
            <div className={css.previewContent}>
              {documentId && (
                <>
                  {!isDocumentsAiEnabled && (
                    <DocumentHeader
                      document={{
                        id: documentId,
                        title: data?.title ?? "",
                        orgs: data?.buyers ?? [],
                        leads: data?.leads ?? [],
                        downloadUrl: data?.downloadUrl ?? "",
                        hasBuyerLeads: data?.hasBuyerLeads ?? false,
                      }}
                      isLoading={isLoading}
                    />
                  )}
                  <DocumentViewer
                    key={documentId}
                    documentId={documentId}
                    filters={filters}
                    isDocumentsAiEnabled={isDocumentsAiEnabled}
                    isDocumentAiSearchEnabled={isDocumentAiSearchEnabled}
                    initialSourceLocations={initialSourceLocations}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </Drawer>
    </TrackingProvider>
  );
}

function DocumentViewer({
  documentId,
  filters,
  isDocumentsAiEnabled,
  isDocumentAiSearchEnabled,
  initialSourceLocations,
}: DocumentViewerProps) {
  const { data, isLoading, isFetching, refetch: refetchDocument } = useDocument(documentId);
  useLogEntityViewOnOpen(LoggedEntityType.BuyerDocument, documentId);
  const defaultFilters = useMemo<DocumentViewerFilters>(
    () => ({ keywords: filters.keywords ?? [] }),
    [filters.keywords],
  );

  const hasKeywords = !!defaultFilters.keywords.length;
  const fallbackTab: TabKey = isDocumentsAiEnabled ? TAB_KEYS.overview : TAB_KEYS.info;
  const defaultTab: TabKey = !isDocumentsAiEnabled && hasKeywords ? TAB_KEYS.search : fallbackTab;
  const [userSelectedTab, setUserSelectedTab] = useState<TabKey | null>(null);

  const isInFallbackMode =
    getDocumentViewerSource(data?.pdfDocumentUrl, data?.downloadUrl, data?.originalMimeType)
      ?.isInFallbackMode ?? false;

  // Search is disabled when no PDF variant exists: keyword highlighting currently relies on a
  // server-side text-search service that runs against the PDF, so the in-browser
  // rendering fallback path (where pdfDocumentUrl is empty) currently can't support it.
  const isSearchDisabled = !isLoading && !data?.pdfDocumentUrl;
  const candidateTab: TabKey = userSelectedTab ?? defaultTab;
  const activeTab: TabKey =
    candidateTab === TAB_KEYS.search && isSearchDisabled ? fallbackTab : candidateTab;

  const tabPanels = useMemo(
    () =>
      createTabPanels(
        documentId,
        isLoading,
        defaultFilters,
        data,
        isDocumentsAiEnabled,
        isDocumentAiSearchEnabled,
        () => void refetchDocument(),
        isInFallbackMode,
      ),
    [
      documentId,
      isLoading,
      defaultFilters,
      data,
      isDocumentsAiEnabled,
      isDocumentAiSearchEnabled,
      refetchDocument,
      isInFallbackMode,
    ],
  );

  const tabItems = createTabItems(
    documentId,
    isLoading,
    isSearchDisabled,
    defaultFilters,
    data,
    isInFallbackMode,
  );

  const tabs = useMemo(
    () =>
      TAB_LABELS.map((tab) =>
        tab.key === TAB_KEYS.search ? { ...tab, disabled: isSearchDisabled } : tab,
      ),
    [isSearchDisabled],
  );

  const pdfDocumentUrl = data?.pdfDocumentUrl ?? "";

  return (
    <FileViewerContextProvider>
      {initialSourceLocations && initialSourceLocations.length > 0 && (
        <JumpToInitialSourceLocations
          locations={initialSourceLocations}
          pdfDocumentUrl={pdfDocumentUrl}
        />
      )}
      {isDocumentsAiEnabled && (
        <DocumentHeader
          document={{
            id: documentId,
            title: data?.title ?? "",
            orgs: data?.buyers ?? [],
            leads: data?.leads ?? [],
            downloadUrl: data?.downloadUrl ?? "",
            hasBuyerLeads: data?.hasBuyerLeads ?? false,
          }}
          isLoading={isLoading}
          activeTab={activeTab}
          onTabChange={setUserSelectedTab}
          tabs={tabs}
        />
      )}
      <div className={css.container}>
        <div className={css.columns}>
          <PdfViewerContainer
            pdfDocumentUrl={pdfDocumentUrl}
            downloadUrl={data?.downloadUrl ?? ""}
            originalMimeType={data?.originalMimeType}
            hasKeywordsFilter={!!defaultFilters.keywords.length}
            isLoading={isFetching}
            containerClassName={css.viewerContainer}
            inDrawer
          />
          {isDocumentsAiEnabled ? (
            <ToolbarContainerNew className={css.toolbarContainer}>
              {tabPanels[activeTab]}
            </ToolbarContainerNew>
          ) : (
            <ToolbarContainerLegacy className={css.toolbarContainer}>
              <Tabs
                value={activeTab}
                items={tabItems}
                onChange={(key) => setUserSelectedTab(key as TabKey)}
              />
            </ToolbarContainerLegacy>
          )}
        </div>
      </div>
    </FileViewerContextProvider>
  );
}

// Highlights + jumps to the given locations once the document's annotations are
// ready. Listens for `annotationsLoaded` (the canonical ready event — see
// DocumentSearch.tsx for why `documentLoaded` is not sufficient) and draws once
// per loaded document. Reset on `documentUrl` change so reopening with a
// different doc re-fires correctly.
function JumpToInitialSourceLocations({
  locations,
  pdfDocumentUrl,
}: {
  locations: SourceLocation[];
  pdfDocumentUrl: string;
}) {
  const { instance } = useFileViewerInstance();
  const [documentReady, setDocumentReady] = useState(false);
  const hasDrawnRef = useRef(false);

  useEffect(() => {
    setDocumentReady(false);
    hasDrawnRef.current = false;
  }, [pdfDocumentUrl]);

  useEffect(() => {
    if (!instance) return;
    const { documentViewer } = instance.Core;
    const onReady = () => setDocumentReady(true);
    documentViewer.addEventListener("annotationsLoaded", onReady);
    return () => documentViewer.removeEventListener("annotationsLoaded", onReady);
  }, [instance]);

  useEffect(() => {
    if (!instance || !documentReady || hasDrawnRef.current) return;
    if (locations.length === 0) return;
    hasDrawnRef.current = true;
    drawSourceAnnotations(instance, locations);
  }, [instance, documentReady, locations]);

  return null;
}
